import React from 'react'
import { useIntl } from 'react-intl'
// eslint-disable-next-line no-restricted-syntax
import { logError, logInfo } from '../../toaster'
import { RouteContext, SMD_SESSION_FILTER_TYPE } from '../../route-provider'
import { dateFormats } from '../../react-intl-helpers'
import {
  AgentSessionType,
  useAgentSessions,
  useOrganizationProfile
} from '../api'

const AGENT_SESSION_ID = 'agentSessionId'
export const useSessionMetadataAction = ({
  applicationUuid,
  organizationUuid,
  onSessionChange,
  filterToggle,
  filtersActive,
  totalAppCustomAgentSession
}) => {
  // state
  const intl = useIntl()
  const {
    addSessionFilters,
    removeSessionFilters,
    setIsMostRecentSession,
    sessionFilters,
    isMostRecentSession
  } = React.useContext(RouteContext)
  const [activeSession, setActiveSession] = React.useState<
    AgentSessionType | undefined
  >()
  const [sessionText, setSessionText] = React.useState<string>()
  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const [isCleared, setIsCleared] = React.useState(false)
  const isActive = !!filtersActive.agentSessionId
  const isRemovableSession = !!activeSession
  const sessionFilterId = sessionFilters
    .get(SMD_SESSION_FILTER_TYPE)
    ?.get(AGENT_SESSION_ID)
  const hasSessionFilters = !!sessionFilterId
  const hasCustomSession = totalAppCustomAgentSession?.customTotal > 0

  const { isLoading: isOrgProfileLoading, data: orgProfile } =
    useOrganizationProfile({ organizationUuid })
  const isDefaultToLatestSession = isOrgProfileLoading
    ? false
    : orgProfile?.organization?.properties
        ?.default_filtering_by_most_recent_session === 'true' ||
      isMostRecentSession // use most recent session if user explictly sets it

  // only request the latest session if the org flag is ON and
  // the user has not set a session filter themselves
  // and we don't already have a session active
  const isLatestAgentSession =
    isDefaultToLatestSession &&
    !hasSessionFilters &&
    !activeSession &&
    !isCleared
  // get session by id when we only have an id
  const isIdAgentSession = isActive && !activeSession && !isCleared

  const { error: currentAgentSessionError, data: currentAgentSession } =
    useAgentSessions({
      applicationUuid,
      organizationUuid,
      agentSessionId: filtersActive.agentSessionId?.[0] || 'latest',
      enabled: isLatestAgentSession || isIdAgentSession
    })

  // events
  const onCancel = React.useCallback(() => {
    setIsModalOpen(false)
  }, [setIsModalOpen])

  const onSubmit = React.useCallback(
    ({ agentSession, isMostRecentSession }) => {
      if (isMostRecentSession) {
        if (hasSessionFilters && activeSession) {
          // toggle off active filter
          filterToggle({
            groupID: AGENT_SESSION_ID,
            optionID: activeSession.agentSessionId
          })

          removeSessionFilters(
            SMD_SESSION_FILTER_TYPE,
            new Map([[AGENT_SESSION_ID, [activeSession.agentSessionId]]])
          )
        }

        // set the override and let currentAgentSession effect fire
        setIsCleared(false)
        setActiveSession(undefined)
        setIsMostRecentSession(true)
        setIsModalOpen(false)

        return
      }

      // set custom session filter

      // is the filter is already set?
      if (filtersActive.agentSessionId?.[0] === agentSession?.agentSessionId) {
        // close dialog to show some response to setting the same session
        setIsModalOpen(false)
        // back out to ensure we don't toggle off the same filter
        return
      }

      // toggle off existing filter, we only keep 1 at a time
      if (filtersActive.agentSessionId?.[0]) {
        filterToggle({
          groupID: AGENT_SESSION_ID,
          optionID: filtersActive.agentSessionId?.[0]
        })

        removeSessionFilters(
          SMD_SESSION_FILTER_TYPE,
          new Map([[AGENT_SESSION_ID, [filtersActive.agentSessionId?.[0]]]])
        )
      }

      filterToggle({
        groupID: AGENT_SESSION_ID,
        optionID: agentSession.agentSessionId
      })

      addSessionFilters(
        SMD_SESSION_FILTER_TYPE,
        new Map([[AGENT_SESSION_ID, [agentSession.agentSessionId]]])
      )

      setIsCleared(false)
      setActiveSession(agentSession)
      setIsMostRecentSession(false)
      setIsModalOpen(false)

      if (typeof onSessionChange === 'function') {
        onSessionChange(agentSession)
      }
    },
    [
      activeSession,
      setActiveSession,
      setIsModalOpen,
      setIsCleared,
      filtersActive,
      filterToggle,
      hasSessionFilters,
      setIsMostRecentSession,
      addSessionFilters,
      removeSessionFilters,
      onSessionChange
    ]
  )

  const onClear = React.useCallback(() => {
    // ensure we never set undefined as a value and
    // only ever toggle an existing filter off
    if (isActive && activeSession?.agentSessionId) {
      filterToggle({
        groupID: AGENT_SESSION_ID,
        optionID: activeSession.agentSessionId
      })

      removeSessionFilters(
        SMD_SESSION_FILTER_TYPE,
        new Map([[AGENT_SESSION_ID, [activeSession.agentSessionId]]])
      )
    }

    if (isDefaultToLatestSession) {
      setIsCleared(true)
    }

    setActiveSession(undefined)
    setIsMostRecentSession(false)
    if (onSessionChange) {
      onSessionChange(undefined)
    }
  }, [
    isActive,
    activeSession,
    setIsCleared,
    isDefaultToLatestSession,
    setActiveSession,
    filterToggle,
    setIsMostRecentSession,
    removeSessionFilters,
    onSessionChange
  ])

  // effects
  React.useEffect(() => {
    if (!currentAgentSession?.agentSession) {
      // the application doesn't have any custom sessions
      if (currentAgentSession?.success && currentAgentSession?.messages) {
        logInfo(currentAgentSession.messages?.join(','))
      }

      return
    }

    if (isLatestAgentSession) {
      // apply most recent session
      if (
        // ensure we do not toggle it off if it is set
        filtersActive.agentSessionId?.[0] !==
        currentAgentSession.agentSession.agentSessionId
      ) {
        filterToggle({
          groupID: AGENT_SESSION_ID,
          optionID: currentAgentSession.agentSession.agentSessionId
        })
      }

      // do not add it to session filters
      // when using most recent session they
      // are loaded by the api not passed in session filters
    } else {
      addSessionFilters(
        SMD_SESSION_FILTER_TYPE,
        new Map([
          [AGENT_SESSION_ID, [currentAgentSession.agentSession.agentSessionId]]
        ])
      )
    }

    setIsCleared(false)
    setActiveSession(currentAgentSession.agentSession)

    if (onSessionChange) {
      onSessionChange({
        ...currentAgentSession.agentSession
      })
    }

    // filterToggle is not stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intl, setActiveSession, isLatestAgentSession, currentAgentSession])

  React.useEffect(() => {
    if (currentAgentSessionError) {
      // an error occurred retrieving session data
      logError(intl.formatMessage({ id: 'detail_agentSessionError' }))
    }
  }, [intl, currentAgentSessionError])

  React.useEffect(() => {
    if (activeSession) {
      setSessionText(
        intl.formatDate(
          activeSession.createdDate * 1000,
          dateFormats.DATETIME_LONG
        )
      )
    }
  }, [intl, activeSession])

  return {
    sessionText,
    isActive,
    isModalOpen,
    setIsModalOpen,
    onClear,
    onSubmit,
    onCancel,
    activeSession,
    isUsingMostRecentSession: isDefaultToLatestSession && !hasSessionFilters,
    isRemovableSession,
    hasCustomSession
  }
}
